// Postęp dziecka na serwerze: sprawdzenie kształtu danych z obcego źródła i scalanie dwóch kopii (urządzenie ↔ serwer).
// Czysta logika bez DOM i sieci — używają jej aplikacja (synchronizacja) i serwer (`PUT /api/progress`).
//
// Scalanie jest przemienne i bezpieczne przy powtórzeniu (merge(a, b) = merge(b, a), merge(a, a) = a), więc kolejność zapisów z dwóch urządzeń
// nie ma znaczenia, a ponowne wysłanie tej samej kopii niczego nie psuje. Reguły:
//  • data ostatniej lekcji: późniejsza;
//  • słówka (SRS): dla każdego słowa wygrywa wpis z późniejszym `lastSeen` (przy remisie — z większą liczbą powtórzeń, potem deterministycznie);
//  • historia: suma wielozbiorów (identyczne wpisy z obu kopii liczą się raz, ale dwie takie same sesje na jednym urządzeniu zostają dwiema), ostatnie 200;
//  • gwiazdki: największa z trzech liczb — obu kopii i sumy gwiazdek ze scalonej historii (suma to dokładny wynik, gdy historia nie była przycięta);
//  • seria: z kopii z późniejszą datą ostatniej lekcji (przy remisie — większa);
//  • kurs (patch-37): ukończone lekcje — suma zbiorów (przy tej samej lekcji wygrywa późniejsza data, potem więcej gwiazdek); słowa kursu jak słówka (SRS);
//    dziennik dnia (patch-47): dla każdej daty suma słów (posortowana) i większa liczba lekcji, najnowsze 60 dni; licznik `laps` pudełka 5 przechodzi razem ze słowem;
//  • nastroje (patch-32): suma dat; dla tej samej daty wygrywa wpis zapisany później (`at`, przy remisie — deterministycznie), trzymamy najnowsze 400 dni.
// Dawne pola starego kursu „dni” — `completedDays` i `items` (patch-49 je usunął) — oraz `day` we wpisach historii i nastrojów są TOLEROWANE na wejściu (starsze urządzenie
// może je jeszcze wysyłać): niczego z nich nie czytamy ani nie zapisujemy, więc znikają z kopii na serwerze przy najbliższym zapisie.
import type { CourseDone, CourseProgress, HistoryEntry, MoodEntry, Progress, SrsItem } from '../types.ts';
import { MAX_MOODS, isMoodId, pruneMoods } from './mood.ts';
import type { DayLogs } from './review.ts';

export const HISTORY_LIMIT = 200;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Ile słów kursu (`course.items`) przyjmujemy w jednym postępie. Słownik CEFR-J A1–C2 ma 8 272 słów treści (SPEC §0.1 pkt 2), więc 10 000 starcza na cały kurs;
 * dotąd limit wynosił 3 000 (pełne A1 to 944 słowa, ale kurs A2 zbliżałby się do niego — podniesione przed pracą nad A2, patch-49).
 * Rozmiar najgorszego realistycznego postępu (cały słownik, długie słowa, pełna historia) mieści się w `MAX_PROGRESS_BODY` serwera — patrz test `progress-merge`.
 */
export const MAX_ITEMS = 10_000;
const MAX_LESSONS = 3000;
/** Ile ostatnich dni zostaje w dzienniku dnia; „Powtórka dnia” potrzebuje 7 dni nauki (limit sesji) i ostatniego dnia (pula świeża). */
export const LOG_DAYS = 60;
export const MAX_LOG_WORDS = 300;
const LESSON_ID = /^[a-z0-9][a-z0-9-]{1,38}$/;

const int = (v: unknown, min: number, max: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const date = (v: unknown): v is string => typeof v === 'string' && DATE.test(v);

function cleanItem(key: string, raw: unknown): SrsItem | null {
  const r = raw as Record<string, unknown> | null;
  if (!r || typeof r !== 'object') return null;
  if (typeof r.word !== 'string' || r.word.length === 0 || r.word.length > 60 || r.word !== key) return null;
  if (!int(r.box, 1, 5) || !date(r.due) || !date(r.lastSeen) || !int(r.correct, 0, 1_000_000) || !int(r.wrong, 0, 1_000_000)) return null;
  if (r.laps !== undefined && !int(r.laps, 0, 10_000)) return null;
  return { word: r.word, box: r.box as SrsItem['box'], due: r.due, lastSeen: r.lastSeen, correct: r.correct, wrong: r.wrong, ...(r.laps !== undefined ? { laps: r.laps as number } : {}) };
}

/** Dziennik dnia z obcego źródła: `undefined` = brak pola, `null` = niepoprawny kształt; w postaci kanonicznej (słowa posortowane, bez powtórzeń). */
function cleanLog(raw: unknown): DayLogs | null | undefined {
  if (raw === undefined) return undefined;
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const dates = Object.keys(raw);
  if (dates.length > LOG_DAYS * 2) return null;
  const out: DayLogs = {};
  for (const d of dates.sort()) {
    const e = (raw as Record<string, unknown>)[d] as Record<string, unknown> | null;
    if (!date(d) || !e || typeof e !== 'object' || !int(e.lessons, 0, 1000) || !Array.isArray(e.words) || e.words.length > MAX_LOG_WORDS * 4) return null;
    if (!e.words.every((w) => typeof w === 'string' && w.length > 0 && w.length <= 60)) return null;
    // nierealnie duży dzień (> 300 słów) przycinamy zamiast odrzucać cały postęp
    out[d] = { lessons: e.lessons, words: [...new Set(e.words as string[])].sort().slice(0, MAX_LOG_WORDS) };
  }
  return pruneLog(out);
}

/** Zostawia najnowsze `LOG_DAYS` dni dziennika. */
export function pruneLog(log: DayLogs): DayLogs {
  const dates = Object.keys(log).sort();
  if (dates.length <= LOG_DAYS) return log;
  const out: DayLogs = {};
  for (const d of dates.slice(-LOG_DAYS)) out[d] = log[d]!;
  return out;
}

function cleanEntry(raw: unknown): HistoryEntry | null {
  const r = raw as Record<string, unknown> | null;
  if (!r || typeof r !== 'object') return null;
  if (!date(r.date) || (r.mode !== 'lesson' && r.mode !== 'review')) return null;
  if (!int(r.stars, 0, 10_000) || !int(r.speakTasks, 0, 1000) || !int(r.clozeCorrect, 0, 1000) || !int(r.clozeTotal, 0, 1000)) return null;
  if (r.day !== undefined && !int(r.day, 1, 9999)) return null; // dawne pole dni — tolerowane, nie zapisujemy
  if (r.lesson !== undefined && !(typeof r.lesson === 'string' && LESSON_ID.test(r.lesson))) return null;
  if (r.percent !== undefined && !int(r.percent, 0, 100)) return null;
  return {
    date: r.date,
    mode: r.mode,
    ...(r.lesson !== undefined ? { lesson: r.lesson as string } : {}),
    stars: r.stars,
    ...(r.percent !== undefined ? { percent: r.percent as number } : {}),
    speakTasks: r.speakTasks,
    clozeCorrect: r.clozeCorrect,
    clozeTotal: r.clozeTotal,
  };
}

function cleanDone(raw: unknown): CourseDone | null {
  const r = raw as Record<string, unknown> | null;
  if (!r || typeof r !== 'object' || !date(r.date) || !int(r.stars, 0, 10_000)) return null;
  if (r.percent !== undefined && !int(r.percent, 0, 100)) return null;
  return { date: r.date, stars: r.stars, ...(r.percent !== undefined ? { percent: r.percent as number } : {}) };
}

/** Postęp kursu v2 z obcego źródła; `undefined` = brak pola, `null` = niepoprawny kształt. */
function cleanCourse(raw: unknown): CourseProgress | null | undefined {
  if (raw === undefined) return undefined;
  const r = raw as Record<string, unknown> | null;
  if (!r || typeof r !== 'object' || Array.isArray(r)) return null;
  const { done: rawDone, items: rawItems } = r;
  if (typeof rawDone !== 'object' || rawDone === null || Array.isArray(rawDone) || typeof rawItems !== 'object' || rawItems === null || Array.isArray(rawItems)) return null;
  const ids = Object.keys(rawDone);
  const words = Object.keys(rawItems);
  if (ids.length > MAX_LESSONS || words.length > MAX_ITEMS) return null;
  const done: Record<string, CourseDone> = {};
  for (const id of ids.sort()) {
    const d = cleanDone((rawDone as Record<string, unknown>)[id]);
    if (!LESSON_ID.test(id) || !d) return null;
    done[id] = d;
  }
  const items: Record<string, SrsItem> = {};
  for (const w of words.sort()) {
    const item = cleanItem(w, (rawItems as Record<string, unknown>)[w]);
    if (!item) return null;
    items[w] = item;
  }
  const log = cleanLog(r.log);
  if (log === null) return null;
  return { done, items, ...(log && Object.keys(log).length > 0 ? { log } : {}) };
}

function cleanMood(raw: unknown): MoodEntry | null {
  const r = raw as Record<string, unknown> | null;
  if (!r || typeof r !== 'object') return null;
  if (!isMoodId(r.mood) || !int(r.at, 0, 9_999_999_999_999)) return null;
  if (r.day !== undefined && !int(r.day, 1, 9999)) return null; // dawne pole dni — tolerowane, nie zapisujemy
  return { mood: r.mood, at: r.at };
}

/**
 * Sprawdza postęp z obcego źródła (żądanie do serwera, odpowiedź serwera, pamięć przeglądarki) i zwraca jego czystą kopię w postaci kanonicznej
 * (posortowane dni i słowa, historia w kolejności). `null` = dane nie mają poprawnego kształtu — nic z nich nie przyjmujemy.
 */
export function validateProgress(raw: unknown): Progress | null {
  const r = raw as Record<string, unknown> | null;
  if (!r || typeof r !== 'object' || r.version !== 1) return null;
  if (r.completedDays !== undefined && (!Array.isArray(r.completedDays) || r.completedDays.length > 2000 || !r.completedDays.every((d) => int(d, 1, 9999)))) return null; // dawne pole — tylko sprawdzamy kształt
  if (r.lastLessonDate !== null && !date(r.lastLessonDate)) return null;
  if (!int(r.streak, 0, 100_000) || !int(r.stars, 0, 10_000_000)) return null;
  if (r.items !== undefined && (typeof r.items !== 'object' || r.items === null || Array.isArray(r.items) || Object.keys(r.items).length > MAX_ITEMS)) return null; // dawne pole — tylko sprawdzamy kształt
  if (!Array.isArray(r.history) || r.history.length > HISTORY_LIMIT) return null;
  const history: HistoryEntry[] = [];
  for (const e of r.history) {
    const entry = cleanEntry(e);
    if (!entry) return null;
    history.push(entry);
  }
  const course = cleanCourse(r.course);
  if (course === null) return null;
  let moods: Record<string, MoodEntry> | undefined;
  if (r.moods !== undefined) {
    if (typeof r.moods !== 'object' || r.moods === null || Array.isArray(r.moods)) return null;
    const dates = Object.keys(r.moods);
    if (dates.length > MAX_MOODS) return null;
    moods = {};
    for (const d of dates.sort()) {
      const m = cleanMood((r.moods as Record<string, unknown>)[d]);
      if (!date(d) || !m) return null;
      moods[d] = m;
    }
  }
  return {
    version: 1,
    lastLessonDate: r.lastLessonDate as string | null,
    streak: r.streak,
    stars: r.stars,
    history,
    ...(moods && Object.keys(moods).length > 0 ? { moods } : {}),
    ...(course && hasCourseData(course) ? { course } : {}),
  };
}

const entryKey = (e: HistoryEntry): string => [e.date, e.mode, e.lesson ?? '', e.stars, e.percent ?? '', e.speakTasks, e.clozeCorrect, e.clozeTotal].join('|');

function betterItem(x: SrsItem, y: SrsItem): SrsItem {
  if (x.lastSeen !== y.lastSeen) return x.lastSeen > y.lastSeen ? x : y;
  const nx = x.correct + x.wrong;
  const ny = y.correct + y.wrong;
  if (nx !== ny) return nx > ny ? x : y;
  const lx = x.laps ?? 0; // licznik pudełka 5 nie może przegrać z kopią starszego klienta, która go nie zna (porównanie JSON-ów dawałoby wynik zależny od znaków)
  const ly = y.laps ?? 0;
  if (lx !== ly) return lx > ly ? x : y;
  return JSON.stringify(x) >= JSON.stringify(y) ? x : y; // remis: deterministycznie, żeby wynik nie zależał od kolejności argumentów
}

function mergeHistory(a: readonly HistoryEntry[], b: readonly HistoryEntry[]): HistoryEntry[] {
  const count = (list: readonly HistoryEntry[]) => {
    const m = new Map<string, { e: HistoryEntry; n: number }>();
    for (const e of list) {
      const k = entryKey(e);
      const slot = m.get(k);
      if (slot) slot.n++;
      else m.set(k, { e, n: 1 });
    }
    return m;
  };
  const ca = count(a);
  const cb = count(b);
  const out: Array<{ key: string; e: HistoryEntry }> = [];
  for (const key of new Set([...ca.keys(), ...cb.keys()])) {
    const e = (ca.get(key) ?? cb.get(key))!.e;
    for (let i = 0; i < Math.max(ca.get(key)?.n ?? 0, cb.get(key)?.n ?? 0); i++) out.push({ key, e });
  }
  out.sort((p, q) => (p.e.date !== q.e.date ? (p.e.date < q.e.date ? -1 : 1) : p.key < q.key ? -1 : p.key > q.key ? 1 : 0));
  return out.map((x) => x.e).slice(-HISTORY_LIMIT);
}

function betterMood(x: MoodEntry, y: MoodEntry): MoodEntry {
  if (x.at !== y.at) return x.at > y.at ? x : y;
  return JSON.stringify(x) >= JSON.stringify(y) ? x : y;
}

function mergeMoods(a: Progress['moods'], b: Progress['moods']): Record<string, MoodEntry> {
  const out: Record<string, MoodEntry> = {};
  for (const d of [...new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})])].sort()) {
    const x = a?.[d];
    const y = b?.[d];
    out[d] = (x && y ? betterMood(x, y) : x ?? y) as MoodEntry;
  }
  return pruneMoods(out);
}

function betterDone(x: CourseDone, y: CourseDone): CourseDone {
  if (x.date !== y.date) return x.date > y.date ? x : y;
  if (x.stars !== y.stars) return x.stars > y.stars ? x : y;
  return JSON.stringify(x) >= JSON.stringify(y) ? x : y;
}

function mergeCourse(a: CourseProgress | undefined, b: CourseProgress | undefined): CourseProgress | undefined {
  if (!a && !b) return undefined;
  const done: Record<string, CourseDone> = {};
  for (const id of [...new Set([...Object.keys(a?.done ?? {}), ...Object.keys(b?.done ?? {})])].sort()) {
    const x = a?.done[id];
    const y = b?.done[id];
    done[id] = (x && y ? betterDone(x, y) : x ?? y) as CourseDone;
  }
  const items: Record<string, SrsItem> = {};
  for (const w of [...new Set([...Object.keys(a?.items ?? {}), ...Object.keys(b?.items ?? {})])].sort()) {
    const x = a?.items[w];
    const y = b?.items[w];
    items[w] = (x && y ? betterItem(x, y) : x ?? y) as SrsItem;
  }
  const log = mergeLogs(a?.log, b?.log);
  return { done, items, ...(Object.keys(log).length > 0 ? { log } : {}) };
}

/** Czy kurs ma cokolwiek do zapisania (puste `course` nie wędruje do kopii). */
function hasCourseData(c: CourseProgress): boolean {
  return Object.keys(c.done).length > 0 || Object.keys(c.items).length > 0 || Object.keys(c.log ?? {}).length > 0;
}

/** Dziennik dnia z dwóch kopii: dla każdej daty suma słów (posortowana) i większa liczba lekcji; przemienne i odporne na powtórzenie. */
function mergeLogs(a: DayLogs | undefined, b: DayLogs | undefined): DayLogs {
  const out: DayLogs = {};
  for (const d of [...new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})])].sort()) {
    const x = a?.[d];
    const y = b?.[d];
    out[d] = { lessons: Math.max(x?.lessons ?? 0, y?.lessons ?? 0), words: [...new Set([...(x?.words ?? []), ...(y?.words ?? [])])].sort().slice(0, MAX_LOG_WORDS) };
  }
  return pruneLog(out);
}

/** Scala dwie kopie postępu tego samego dziecka (patrz reguły na górze pliku). Obie muszą być poprawne (`validateProgress`). */
export function mergeProgress(a: Progress, b: Progress): Progress {
  const history = mergeHistory(a.history, b.history);
  const lastLessonDate = a.lastLessonDate === null ? b.lastLessonDate : b.lastLessonDate === null ? a.lastLessonDate : a.lastLessonDate >= b.lastLessonDate ? a.lastLessonDate : b.lastLessonDate;
  const streak = (a.lastLessonDate ?? '') === (b.lastLessonDate ?? '') ? Math.max(a.streak, b.streak) : (a.lastLessonDate ?? '') > (b.lastLessonDate ?? '') ? a.streak : b.streak;
  const moods = mergeMoods(a.moods, b.moods);
  const course = mergeCourse(a.course, b.course);
  return {
    version: 1,
    lastLessonDate,
    streak,
    stars: Math.max(a.stars, b.stars, history.reduce((n, e) => n + e.stars, 0)),
    history,
    ...(Object.keys(moods).length > 0 ? { moods } : {}),
    ...(course && hasCourseData(course) ? { course } : {}),
  };
}

/** Krótkie podsumowanie do listy w panelu rodzica: lekcje i słówka kursu. */
export function progressSummary(p: Progress): { lessonsDone: number; words: number; stars: number; streak: number; lastLessonDate: string | null } {
  return { lessonsDone: Object.keys(p.course?.done ?? {}).length, words: Object.keys(p.course?.items ?? {}).length, stars: p.stars, streak: p.streak, lastLessonDate: p.lastLessonDate };
}
